import {
  filterProductsByCabinCategoryType,
  filterProductsByCabinType,
  filterProductsByInboundAirport,
  filterProductsByOutboundAirport,
  filterProductsByTariffType,
} from '../filters';
import { isTariff } from '../guards';

import { getAirportsFromProductList } from './flights';
import { getTotalPrice } from './price';

import type { CabinType, ProductWithServices, Tariff } from '../types';

type ScopeParams = {
  inboundAirport?: string;
  outboundAirport?: string;
  tariff?: string;
  cabinType?: CabinType;
  cabinCategoryType?: string;
};

type CabinScopeParams = Pick<
  ScopeParams,
  'tariff' | 'cabinType' | 'cabinCategoryType'
>;

export const scopeProducts = (
  products: ProductWithServices[],
  params: ScopeParams,
): ProductWithServices[] => {
  let { inboundAirport, outboundAirport, tariff, cabinCategoryType } = params;
  const { cabinType } = params;

  let filtered = [...products];

  inboundAirport = inboundAirport === '' ? undefined : inboundAirport;
  outboundAirport = outboundAirport === '' ? undefined : outboundAirport;
  tariff = tariff === '' ? undefined : tariff;
  cabinCategoryType = cabinCategoryType === '' ? undefined : cabinCategoryType;

  if (typeof inboundAirport !== 'undefined') {
    filtered = filterProductsByInboundAirport(filtered, inboundAirport);
  }

  if (typeof outboundAirport !== 'undefined') {
    filtered = filterProductsByOutboundAirport(filtered, outboundAirport);
  }

  if (
    typeof cabinCategoryType !== 'undefined' ||
    typeof cabinType !== 'undefined'
  ) {
    return scopeProductsToCabin(filtered, {
      tariff,
      cabinType,
      cabinCategoryType,
    });
  }

  if (isTariff(tariff)) {
    filtered = filterProductsByTariffType(filtered, tariff);
  }

  // if (typeof cabinCategoryType !== 'undefined') {
  //   let cabinCategoryTypeFiltered = filterProductsByCabinCategoryType(
  //     filtered,
  //     cabinCategoryType,
  //   );
  //
  //   if (cabinCategoryTypeFiltered.length > 0) {
  //     return cabinCategoryTypeFiltered;
  //   }
  // }
  //
  // if (typeof cabinType !== 'undefined') {
  //   return filterProductsByCabinType(filtered, cabinType);
  // }

  return filtered;
};

export const scopeToCheapestAirportCombination = (
  products: ProductWithServices[],
) => {
  const inbound = getAirportsFromProductList(products, 'inbound');
  const outbound = getAirportsFromProductList(products, 'outbound');

  const airports = inbound.length < outbound.length ? inbound : outbound;

  const codesWithPrices = airports
    .map(airport => airport.iataCode)
    .reduce<[string, number][]>((collection, code) => {
      const scoped = scopeProducts(products, {
        inboundAirport: code,
        outboundAirport: code,
      });

      const price = getTotalPrice(scoped[0]) ?? Infinity;

      return [...collection, [code, price]];
    }, [])
    .sort(([aCode, aPrice], [bCode, bPrice]) => {
      if (aPrice !== bPrice) {
        return aPrice - bPrice;
      }

      return String(aCode).localeCompare(bCode);
    });

  const [winnerCode, winnerPrice] = codesWithPrices[0];

  if (winnerPrice === Infinity) {
    return products;
  }

  return scopeProducts(products, {
    inboundAirport: winnerCode,
    outboundAirport: winnerCode,
  });
};

export const scopeProductsToCabin = (
  products: ProductWithServices[],
  params: CabinScopeParams,
  options?: {
    strictTariffScope?: boolean;
  },
): ProductWithServices[] => {
  if (products.length === 0) {
    return [];
  }

  const { tariff, cabinType, cabinCategoryType } = params;

  const { strictTariffScope = false } = options ?? {};

  const scoped: Record<Tariff, ProductWithServices[]> = {
    PUR: filterProductsByTariffType(products, 'PUR'),
    PLUS: filterProductsByTariffType(products, 'PLUS'),
    PRO: filterProductsByTariffType(products, 'PRO'),
  };

  const keys = Object.keys(scoped) as Array<keyof typeof scoped>;

  keys.forEach(key => {
    if (typeof cabinCategoryType !== 'undefined') {
      const filtered = filterProductsByCabinCategoryType(
        scoped[key],
        cabinCategoryType,
      );

      if (filtered.length > 0) {
        scoped[key] = filtered;
        return;
      }
    }

    if (typeof cabinType === 'undefined') {
      return;
    }

    scoped[key] = filterProductsByCabinType(scoped[key], cabinType);
  });

  if (strictTariffScope) {
    return isTariff(tariff) ? scoped[tariff] : [];
  }

  if (isTariff(tariff) && scoped[tariff].length > 0) {
    return scoped[tariff];
  }

  //
  // Neue Logik nach Absprache in (EC-1689): Wenn kein Tarif explizit
  // gewünscht ist wird der günstigste Tarif als Standart gewählt
  //
  const cheapestTariffProducts = Object.values(scoped).sort(
    (productsA, productsB) => {
      const getCheapestPrice = (products: ProductWithServices[]) => {
        return Math.min(
          ...products.map(
            product => product.priceInformation?.amount ?? Infinity,
          ),
        );
      };

      return getCheapestPrice(productsA) - getCheapestPrice(productsB);
    },
  )[0];

  return cheapestTariffProducts ?? [];

  // if (scoped.PRO.length > 0) {
  //   return scoped.PRO;
  // }
  //
  // if (scoped.PLUS.length > 0) {
  //   return scoped.PLUS;
  // }
  //
  // return scoped.PUR;
};

export const scopeProductsOptionally = (
  products: ProductWithServices[],
  params: ScopeParams,
): ProductWithServices[] => {
  const {
    inboundAirport,
    outboundAirport,
    tariff,
    cabinType,
    cabinCategoryType,
  } = params;

  let filtered = [...products];

  filtered = applyFilterIfItHasResults(filtered, products => {
    return inboundAirport
      ? filterProductsByInboundAirport(products, inboundAirport)
      : products;
  });

  filtered = applyFilterIfItHasResults(filtered, products => {
    return outboundAirport
      ? filterProductsByOutboundAirport(products, outboundAirport)
      : products;
  });

  if (
    typeof cabinCategoryType !== 'undefined' ||
    typeof cabinType !== 'undefined'
  ) {
    filtered = applyFilterIfItHasResults(filtered, products => {
      return scopeProductsToCabin(products, {
        tariff,
        cabinType,
        cabinCategoryType,
      });
    });
  }

  filtered = applyFilterIfItHasResults(filtered, products => {
    return isTariff(tariff)
      ? filterProductsByTariffType(products, tariff)
      : products;
  });

  // if (typeof cabinCategoryType !== 'undefined') {
  //   let cabinCategoryTypeFiltered = filterProductsByCabinCategoryType(
  //     filtered,
  //     cabinCategoryType,
  //   );
  //
  //   if (cabinCategoryTypeFiltered.length > 0) {
  //     return cabinCategoryTypeFiltered;
  //   }
  // }
  //
  // if (typeof cabinType !== 'undefined') {
  //   return filterProductsByCabinType(filtered, cabinType);
  // }

  return filtered;
};

const applyFilterIfItHasResults = (
  products: ProductWithServices[],

  filter: (_products: ProductWithServices[]) => ProductWithServices[],
) => {
  const filtered = filter(products);

  return filtered.length > 0 ? filtered : products;
};
